import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { DropdownOption, GlobalDropdownField } from '../../../components/GlobalComponents/GlobalDropdownField';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { useBatchStore } from '../../../stateManagers/stores/batchStore';
import { useCourseStore } from '../../../stateManagers/stores/courseStore';

const C = {
  navy: '#0e225d', navyMid: '#1a3370', navyDark: '#0a1a3d',
  gold: '#fbb21f', goldLight: '#ffc93c',
  goldDim: 'rgba(251,178,31,0.18)', goldBorder: 'rgba(251,178,31,0.35)',
  white: '#ffffff', border: '#E5E7EB', bg: '#F5F7FA',
  muted: '#9ca3af', sub: '#6b7280', body: '#1f2937',
};

interface EnrollmentModalProps {
  visible: boolean;
  studentId: string;
  studentName?: string;
  instituteId?: string;
  branchId?: string;
  existingEnrollments?: Array<{ course_id: string; batch_id: string; date_of_admission: string }>;
  onHide: () => void;
  onSuccess?: () => void;
}

interface CourseEnrollment {
  id: string;
  course_id: string;
  batch_id: string;
  date_of_admission: Date;
}

interface BulkCreatedItem {
  index: number;
  student_id: string;
  enrollment_id: string;
  enrollment_no: string;
}
interface BulkFailedItem {
  index: number;
  student_id: string | null;
  error: string;
}
interface BulkEnrollResponse {
  success: boolean;
  summary: { total: number; created: number; failed: number };
  created: BulkCreatedItem[];
  failed: BulkFailedItem[];
}

const StudentEnrollmentModal: React.FC<EnrollmentModalProps> = ({
  visible, studentId, studentName, instituteId, branchId,
  existingEnrollments = [], onHide, onSuccess,
}) => {
  const { courses, fetchCourses } = useCourseStore();
  const { batches, fetchBatches } = useBatchStore();
  const toastRef = useRef<Toast>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [generatedNos, setGeneratedNos] = useState<string[]>([]);
  const [partialFailed, setPartialFailed] = useState<BulkFailedItem[]>([]);
  const [enrollments, setEnrollments] = useState<CourseEnrollment[]>([
    { id: '1', course_id: '', batch_id: '', date_of_admission: new Date() },
  ]);
  const [errors, setErrors] = useState<Record<string, Record<string, string>>>({});
  const [enrolledCourseIds, setEnrolledCourseIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (visible && existingEnrollments?.length > 0) {
      setEnrolledCourseIds(new Set(existingEnrollments.map(e => e.course_id)));
    } else {
      setEnrolledCourseIds(new Set());
    }
  }, [visible, existingEnrollments]);

  useEffect(() => {
    if (visible && (!instituteId || !branchId)) {
      const msg = 'Institute or Branch information is missing. Please try again.';
      setError(msg);
      toastRef.current?.show({ severity: 'error', summary: 'Missing Information', detail: msg, life: 5000 });
    }
  }, [visible, instituteId, branchId]);

  useEffect(() => {
    if (visible) {
      if (!courses) fetchCourses();
      if (!batches) fetchBatches();
    }
  }, [visible, courses, batches, fetchCourses, fetchBatches]);

  const courseOptions: DropdownOption[] = courses
    ? courses.map(course => {
        const isEnrolled = enrolledCourseIds.has(course.course_id);
        return {
          label: `${course.name} - ${course.title}${isEnrolled ? ' (Already Enrolled)' : ''}`,
          value: course.course_id,
          disabled: isEnrolled,
        };
      })
    : [];

  const getFilteredBatches = (courseId: string): DropdownOption[] => {
    if (!courseId || !batches) return [];
    return batches
      .filter(b => b.course_id === courseId && b.is_active)
      .map(b => ({ label: `${b.batch_name} (${b.batch_time})`, value: b.batch_id }));
  };

  const handleInputChange = (enrollmentId: string, field: string, value: any) => {
    setEnrollments(prev => prev.map(e => {
      if (e.id !== enrollmentId) return e;
      const updated = { ...e, [field]: value };
      if (field === 'course_id' && e.course_id !== value) updated.batch_id = '';
      return updated;
    }));
    if (errors[enrollmentId]?.[field]) {
      setErrors(prev => ({ ...prev, [enrollmentId]: { ...prev[enrollmentId], [field]: '' } }));
    }
    setError(null);
    setSuccess(null);
    setGeneratedNos([]);
    setPartialFailed([]);
  };

  const addEnrollment = () => {
    setEnrollments(prev => [
      ...prev,
      { id: String(Date.now()), course_id: '', batch_id: '', date_of_admission: new Date() },
    ]);
  };

  const removeEnrollment = (enrollmentId: string) => {
    if (enrollments.length === 1) {
      const msg = 'At least one course enrollment is required';
      setError(msg);
      toastRef.current?.show({ severity: 'warn', summary: 'Cannot Remove', detail: msg, life: 3000 });
      return;
    }
    setEnrollments(prev => prev.filter(e => e.id !== enrollmentId));
    setErrors(prev => { const n = { ...prev }; delete n[enrollmentId]; return n; });
  };

  const formatDateForAPI = (date: Date): string => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) date = new Date();
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, Record<string, string>> = {};
    let isValid = true;

    if (!instituteId || !branchId) {
      const msg = 'Institute or Branch information is missing';
      setError(msg);
      toastRef.current?.show({ severity: 'error', summary: 'Missing Information', detail: msg, life: 5000 });
      return false;
    }

    const courseIds = enrollments.map(e => e.course_id).filter(Boolean);
    if (courseIds.filter((id, i) => courseIds.indexOf(id) !== i).length > 0) {
      const msg = 'Cannot enroll in the same course multiple times';
      setError(msg);
      toastRef.current?.show({ severity: 'warn', summary: 'Duplicate Course', detail: msg, life: 4000 });
      return false;
    }
    if (courseIds.some(id => enrolledCourseIds.has(id))) {
      const msg = 'Cannot enroll in courses the student is already enrolled in';
      setError(msg);
      toastRef.current?.show({ severity: 'warn', summary: 'Already Enrolled', detail: msg, life: 4000 });
      return false;
    }

    enrollments.forEach(enrollment => {
      const e: Record<string, string> = {};
      if (!enrollment.course_id)         { e.course_id         = 'Please select a course';       isValid = false; }
      if (!enrollment.batch_id)          { e.batch_id          = 'Please select a batch';        isValid = false; }
      if (!enrollment.date_of_admission) { e.date_of_admission = 'Please select admission date'; isValid = false; }
      if (Object.keys(e).length > 0) newErrors[enrollment.id] = e;
    });

    setErrors(newErrors);
    return isValid;
  };

  // ── Single bulk POST ──────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validateForm()) {
      toastRef.current?.show({ severity: 'warn', summary: 'Validation Error', detail: 'Please fill all required fields correctly', life: 4000 });
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setGeneratedNos([]);
    setPartialFailed([]);

    try {
      const response = await apiClient.post<BulkEnrollResponse>('/v1/enrollments/bulk', {
        institute_id: instituteId,
        branch_id: branchId,
        enrollments: enrollments.map(enrollment => ({
          student_id:        studentId,
          course_id:         enrollment.course_id,
          batch_id:          enrollment.batch_id,
          date_of_admission: formatDateForAPI(enrollment.date_of_admission),
        })),
      });

      const { summary, created, failed } = response.data;
      const nos = created.map(c => c.enrollment_no).filter(Boolean);
      if (nos.length > 0) setGeneratedNos(nos);
      if (failed.length > 0) setPartialFailed(failed);

      if (summary.created === summary.total) {
        const successMsg = `Successfully enrolled in ${summary.created} course${summary.created > 1 ? 's' : ''}!`;
        setSuccess(successMsg);
        toastRef.current?.show({
          severity: 'success', summary: 'Enrollment Successful',
          detail: nos.length > 0
            ? `${successMsg} Enrollment No${nos.length > 1 ? 's' : ''}: ${nos.join(', ')}`
            : successMsg,
          life: 4000,
        });
        setTimeout(() => { handleClose(); if (onSuccess) onSuccess(); }, 2500);

      } else if (summary.created > 0) {
        const partialMsg = `Enrolled in ${summary.created} course${summary.created > 1 ? 's' : ''}`;
        const failMsg    = `${summary.failed} enrollment${summary.failed > 1 ? 's' : ''} failed`;
        setSuccess(partialMsg);
        setError(failMsg);
        toastRef.current?.show({ severity: 'warn', summary: 'Partial Enrollment', detail: `${partialMsg}. ${failMsg}.`, life: 5000 });
        if (onSuccess) onSuccess();

      } else {
        const firstErr = failed[0]?.error || 'Failed to enroll student';
        setError(firstErr);
        toastRef.current?.show({ severity: 'error', summary: 'Enrollment Failed', detail: firstErr, life: 5000 });
      }

    } catch (err: any) {
      const errMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to enroll student';
      setError(errMsg);
      toastRef.current?.show({ severity: 'error', summary: 'Enrollment Failed', detail: errMsg, life: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEnrollments([{ id: '1', course_id: '', batch_id: '', date_of_admission: new Date() }]);
    setErrors({});
    setError(null);
    setSuccess(null);
    setGeneratedNos([]);
    setPartialFailed([]);
    setEnrolledCourseIds(new Set());
    onHide();
  };

  const canEnroll = !!(instituteId && branchId);
  const hasAvailableCourses = courseOptions.some(c => !c.disabled);
  const readyCount = enrollments.filter(e => e.course_id && e.batch_id).length;

  return (
    <>
      <Toast ref={toastRef} position="top-right" />
      <Dialog
        visible={visible} onHide={handleClose}
        style={{ width: '90vw', maxWidth: '800px' }}
        header={null} dismissableMask draggable={false}
        pt={{
          root:    { style: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(14,34,93,0.3)' } },
          header:  { style: { display: 'none' } },
          content: { style: { padding: '24px', maxHeight: '90vh', overflowY: 'auto', background: C.bg } },
        }}
      >
        <div>
          {/* ── Hero ─────────────────────────────────────────────────────── */}
          <div style={{
            background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
            borderRadius: '16px', padding: '24px', marginBottom: '20px',
            color: C.white, position: 'relative', overflow: 'hidden',
            boxShadow: '0 8px 24px rgba(14,34,93,0.2)',
          }}>
            <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '160px', height: '160px', background: C.goldDim, borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: '-30px', left: '25%', width: '100px', height: '100px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(30px)', pointerEvents: 'none' }} />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '20px', marginBottom: '10px', fontSize: '0.65rem', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: C.gold }}>
                <i className="pi pi-book" style={{ fontSize: '0.65rem' }} /> Student Enrollment
              </div>
              <h2 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: '700', color: C.white, letterSpacing: '-0.3px' }}>Enroll Student</h2>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem' }}>
                {studentName ? `Enrolling ${studentName} in courses` : 'Enroll student in one or more courses'}
              </p>
              {enrolledCourseIds.size > 0 && (
                <div style={{ marginTop: '12px', padding: '8px 14px', background: C.goldDim, border: `1px solid ${C.goldBorder}`, borderRadius: '8px', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '6px', color: C.gold, fontWeight: '600' }}>
                  <i className="pi pi-info-circle" />
                  Already enrolled in {enrolledCourseIds.size} course{enrolledCourseIds.size > 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>

          {/* ── Success ──────────────────────────────────────────────────── */}
          {success && (
            <div style={{ marginBottom: '16px' }}>
              <Message severity="success" text={success} style={{ fontSize: '0.9rem', fontWeight: '500', width: '100%' }} />
              {generatedNos.length > 0 && (
                <div style={{ marginTop: '12px', padding: '14px 18px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg,#166534,#22c55e)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className="pi pi-check" style={{ color: C.white, fontSize: '0.85rem' }} />
                    </div>
                    <p style={{ margin: 0, fontSize: '0.7rem', fontWeight: '700', color: '#166534', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Enrollment No{generatedNos.length > 1 ? 's' : ''} Generated
                    </p>
                  </div>
                  {generatedNos.map((no, i) => (
                    <p key={i} style={{ margin: '4px 0 0 0', fontSize: '1rem', fontWeight: '700', color: '#15803d', fontFamily: 'monospace' }}>{no}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Error + per-row failures ──────────────────────────────────── */}
          {error && (
            <div style={{ marginBottom: '16px' }}>
              <Message severity="error" text={error} style={{ fontSize: '0.9rem', fontWeight: '500', width: '100%' }} />
              {partialFailed.length > 0 && (
                <div style={{ marginTop: '10px', padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '0.72rem', fontWeight: '700', color: '#991b1b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    Failed Rows
                  </p>
                  {partialFailed.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: i > 0 ? '6px' : 0 }}>
                      <i className="pi pi-times-circle" style={{ color: '#dc2626', fontSize: '0.75rem', flexShrink: 0 }} />
                      <span style={{ fontSize: '0.8rem', color: '#7f1d1d' }}>Row {f.index + 1}: {f.error}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── All courses enrolled ──────────────────────────────────────── */}
          {canEnroll && !hasAvailableCourses && (
            <div style={{ padding: '20px', background: '#fffbeb', border: `2px solid ${C.gold}`, borderRadius: '14px', marginBottom: '20px', textAlign: 'center' }}>
              <div style={{ width: '48px', height: '48px', background: C.goldDim, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <i className="pi pi-info-circle" style={{ fontSize: '1.4rem', color: C.gold }} />
              </div>
              <p style={{ margin: '0 0 6px 0', fontSize: '1rem', fontWeight: '700', color: C.navy }}>All Courses Enrolled</p>
              <p style={{ margin: 0, fontSize: '0.85rem', color: C.sub }}>This student is already enrolled in all available courses.</p>
            </div>
          )}

          {/* ── Form rows ─────────────────────────────────────────────────── */}
          {canEnroll && hasAvailableCourses ? (
            <div style={{ marginBottom: '20px' }}>
              {enrollments.map((enrollment, index) => {
                const filteredBatches  = getFilteredBatches(enrollment.course_id);
                const enrollmentErrors = errors[enrollment.id] || {};
                const courseNumber     = enrolledCourseIds.size + index + 1;
                return (
                  <div key={enrollment.id} style={{ background: C.white, borderRadius: '14px', padding: '20px', marginBottom: '14px', border: `1px solid ${C.border}`, boxShadow: '0 2px 8px rgba(14,34,93,0.05)', position: 'relative' }}>
                    <div style={{ position: 'absolute', top: '16px', right: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div style={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`, color: C.white, padding: '4px 12px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.3px' }}>
                        Course {courseNumber}
                      </div>
                      {enrollments.length > 1 && (
                        <button onClick={() => removeEnrollment(enrollment.id)}
                          style={{ background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', transition: 'all 0.2s' }}
                          onMouseOver={e => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = C.white; }}
                          onMouseOut={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
                        ><i className="pi pi-times" /></button>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '8px' }}>
                      <GlobalDropdownField label="Course" required value={enrollment.course_id}
                        onChange={val => handleInputChange(enrollment.id, 'course_id', val)}
                        options={courseOptions} placeholder="Select a course" icon="pi pi-book" error={enrollmentErrors.course_id} />
                      <GlobalDropdownField label="Batch" required value={enrollment.batch_id}
                        onChange={val => handleInputChange(enrollment.id, 'batch_id', val)}
                        options={filteredBatches} placeholder={enrollment.course_id ? 'Select a batch' : 'Select course first'}
                        icon="pi pi-users" error={enrollmentErrors.batch_id}
                        disabled={!enrollment.course_id || filteredBatches.length === 0} />
                      {enrollment.course_id && filteredBatches.length === 0 && (
                        <div style={{ padding: '12px 14px', background: '#fffbeb', border: `1px solid ${C.gold}`, borderRadius: '8px', fontSize: '0.82rem', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <i className="pi pi-info-circle" style={{ color: C.gold }} /> No active batches available for this course
                        </div>
                      )}
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.75rem', fontWeight: '700', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Date of Admission <span style={{ color: '#dc2626' }}>*</span>
                        </label>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                          <i className="pi pi-calendar" style={{ position: 'absolute', left: '12px', color: C.muted, fontSize: '0.9rem', zIndex: 1 }} />
                          <Calendar value={enrollment.date_of_admission}
                            onChange={e => handleInputChange(enrollment.id, 'date_of_admission', e.value)}
                            dateFormat="dd/mm/yy" showIcon={false} placeholder="Select admission date"
                            style={{ width: '100%' }}
                            inputStyle={{ width: '100%', padding: '11px 12px 11px 40px', border: enrollmentErrors.date_of_admission ? '1.5px solid #dc2626' : `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '0.9rem', outline: 'none', background: C.white, color: C.body }}
                            maxDate={new Date()} />
                        </div>
                        {enrollmentErrors.date_of_admission && (
                          <small style={{ display: 'block', marginTop: '6px', color: '#dc2626', fontSize: '0.75rem', fontWeight: '500' }}>{enrollmentErrors.date_of_admission}</small>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              <button onClick={addEnrollment} disabled={loading}
                style={{ width: '100%', padding: '12px', background: 'transparent', border: `2px dashed ${C.goldBorder}`, borderRadius: '12px', color: C.navy, fontSize: '0.88rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s' }}
                onMouseOver={e => { e.currentTarget.style.background = C.goldDim; e.currentTarget.style.borderColor = C.gold; }}
                onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = C.goldBorder; }}
              >
                <i className="pi pi-plus-circle" style={{ color: C.gold }} /> Add Another Course
              </button>

              {readyCount > 0 && (
                <div style={{ marginTop: '16px', padding: '16px', background: C.white, borderRadius: '14px', border: `1px dashed ${C.goldBorder}`, boxShadow: '0 2px 8px rgba(14,34,93,0.04)' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '0.82rem', fontWeight: '700', color: C.navy, display: 'flex', alignItems: 'center', gap: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <i className="pi pi-check-circle" style={{ color: C.gold }} />
                    New Enrollment Summary ({readyCount} new course{readyCount > 1 ? 's' : ''})
                  </h4>
                  {enrolledCourseIds.size > 0 && (
                    <p style={{ margin: '0 0 12px 0', fontSize: '0.75rem', color: C.muted, fontStyle: 'italic' }}>
                      Total after enrollment: {enrolledCourseIds.size + readyCount} courses
                    </p>
                  )}
                  {enrollments.map((enrollment, index) => {
                    if (!enrollment.course_id || !enrollment.batch_id) return null;
                    const fb = getFilteredBatches(enrollment.course_id);
                    const courseName = courseOptions.find(c => c.value === enrollment.course_id)?.label || '-';
                    const batchName  = fb.find(b => b.value === enrollment.batch_id)?.label || '-';
                    const courseNumber = enrolledCourseIds.size + index + 1;
                    return (
                      <div key={enrollment.id} style={{ fontSize: '0.85rem', lineHeight: '1.6', padding: '12px 14px', background: C.bg, borderRadius: '10px', marginBottom: index < enrollments.length - 1 ? '8px' : 0, border: `1px solid ${C.border}` }}>
                        <div style={{ fontWeight: '700', color: C.navy, marginBottom: '6px', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Course {courseNumber}</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: C.sub }}><span>Course</span><span style={{ fontWeight: '600', color: C.body }}>{courseName}</span></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: C.sub }}><span>Batch</span><span style={{ fontWeight: '600', color: C.body }}>{batchName}</span></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', color: C.sub }}><span>Date</span><span style={{ fontWeight: '600', color: C.body }}>{formatDateForAPI(enrollment.date_of_admission)}</span></div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          ) : !canEnroll ? (
            <div style={{ padding: '24px', textAlign: 'center', background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, marginBottom: '20px' }}>
              <div style={{ width: '48px', height: '48px', background: C.goldDim, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <i className="pi pi-exclamation-triangle" style={{ fontSize: '1.4rem', color: C.gold }} />
              </div>
              <p style={{ margin: 0, color: C.sub, fontSize: '0.9rem' }}>Institute or Branch information is missing. Please ensure you're logged in properly.</p>
            </div>
          ) : null}

          {/* ── Actions ──────────────────────────────────────────────────── */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap', paddingTop: '4px' }}>
            <button onClick={handleClose} disabled={loading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 24px', background: 'transparent', border: `1.5px solid ${C.navy}`, borderRadius: '10px', color: C.navy, fontSize: '0.88rem', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', transition: 'all 0.2s', opacity: loading ? 0.5 : 1 }}
              onMouseEnter={e => { if (!loading) { e.currentTarget.style.background = C.navy; (e.currentTarget as HTMLButtonElement).style.color = C.white; } }}
              onMouseLeave={e => { if (!loading) { e.currentTarget.style.background = 'transparent'; (e.currentTarget as HTMLButtonElement).style.color = C.navy; } }}
            ><i className="pi pi-times" style={{ fontSize: '0.88rem' }} /> Cancel</button>

            <button onClick={handleSubmit} disabled={loading || !canEnroll || !hasAvailableCourses}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '11px 24px', background: (loading || !canEnroll || !hasAvailableCourses) ? C.muted : C.gold, border: 'none', borderRadius: '10px', color: (loading || !canEnroll || !hasAvailableCourses) ? C.white : C.navy, fontSize: '0.88rem', fontWeight: '700', cursor: (loading || !canEnroll || !hasAvailableCourses) ? 'not-allowed' : 'pointer', boxShadow: (loading || !canEnroll || !hasAvailableCourses) ? 'none' : '0 4px 12px rgba(251,178,31,0.4)', transition: 'all 0.2s' }}
              onMouseEnter={e => { if (!loading && canEnroll && hasAvailableCourses) { e.currentTarget.style.background = C.goldLight; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(251,178,31,0.5)'; } }}
              onMouseLeave={e => { if (!loading && canEnroll && hasAvailableCourses) { e.currentTarget.style.background = C.gold; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(251,178,31,0.4)'; } }}
            >
              <i className={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'} style={{ fontSize: '0.88rem' }} />
              {loading ? 'Enrolling...' : `Enroll in ${enrollments.length} Course${enrollments.length > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default StudentEnrollmentModal;